import React from 'react'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { Box, Button, Grid, IconButton, Link, Paper, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import HomeIcon from '@mui/icons-material/Home'
import Refresh from '@mui/icons-material/Refresh'
import DeleteOutline from '@mui/icons-material/DeleteOutline'

import {
  useGetAccountChargeExclusionsQuery,
  useRemoveAccountChargeExclusionMutation
} from '../../../../api-services/asset-holdings/accountChargeRunApi'
import { type AccountChargeExclusionDto } from '../../../../api-schema-types/asset-holdings/AccountChargeExclusionDtos'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import AddAccountChargeExclusionDialog from './AddAccountChargeExclusionDialog'

const AccountChargeExclusionsPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const getExclusions = useGetAccountChargeExclusionsQuery(undefined)
  const [apiRemoveExclusion] = useRemoveAccountChargeExclusionMutation()

  const handleRemove = (exclusion: AccountChargeExclusionDto): void => {
    const choice = window.confirm(
      `Remove ${exclusion.assetHolderName} (${exclusion.assetHolderId}) from the exclusion list? They will be included in the next charge run.`
    )
    if (!choice) {
      return
    }
    apiRemoveExclusion(exclusion.id)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Member has been removed from the exclusion list.' }))
      })
      .catch(error => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(error) }))
      })
  }

  const columns: GridColDef[] = [
    {
      field: 'assetHolderId',
      headerName: 'Member ID',
      flex: 15,
      renderCell: params => (
        <Link component={RouterLink} to={`/app/asset-holder/${params.row.assetHolderId}`}>{params.row.assetHolderId}</Link>
      )
    },
    { field: 'assetHolderName', headerName: 'Name', flex: 30 },
    { field: 'assetHolderEmail', headerName: 'Email', flex: 40 },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 60,
      renderCell: params => (<Tooltip title={params.value}><span>{params.value}</span></Tooltip>)
    },
    {
      field: 'createdByName',
      headerName: 'Added By',
      flex: 30,
      renderCell: params => (
        <Tooltip title={params.row.createdByEmail}><span>{params.row.createdByName !== '' ? params.row.createdByName : 'System'}</span></Tooltip>
      )
    },
    { field: 'createdAtFormatted', headerName: 'Added At', flex: 30 },
    {
      field: 'actions',
      headerName: '',
      flex: 10,
      sortable: false,
      renderCell: params => (
        <Tooltip title="Remove from exclusion list">
          <IconButton color="error" onClick={() => { handleRemove(params.row as AccountChargeExclusionDto) }}>
            <DeleteOutline />
          </IconButton>
        </Tooltip>
      )
    }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Account Charge Run Exclusions</Typography>
        <Button variant={'text'} type='button' onClick={() => { history.push('/app/finance-area') }}>
          <HomeIcon style={{ color: '#444' }} />
        </Button>
      </Box>

      <Paper>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2 }}>
          <Grid item>
            <AddAccountChargeExclusionDialog />
          </Grid>
          <Grid item>
            <Button variant={'contained'} type="button" onClick={() => { void getExclusions.refetch() }}>
              <Refresh/>
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ p: 3 }}>
          <Typography variant={'body1'} sx={{ mb: 2 }}>
            Members listed here are skipped by the monthly account charge run (and by manual charges) until they are
            removed from this list.
          </Typography>

          {getExclusions.isLoading && <LoadingComponent />}
          {getExclusions.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getExclusions.error)} />}
          {getExclusions.isSuccess &&
            <DataGrid
              getRowId={(row) => row.id}
              rows={getExclusions.data}
              columns={columns}
              autoHeight={true}
              disableSelectionOnClick
              pagination
              pageSize={25}
              rowsPerPageOptions={[25]}
            />
          }
        </Box>
      </Paper>
    </>
  )
}

export default AccountChargeExclusionsPage
